// SPDX-License-Identifier: AGPL-3.0-or-later
// Opens a work account sign-in (Microsoft, ClickUp). The authorize URL only arrives after a
// request, and by then iOS no longer lets window.open run, so the phone apps never opened it.
// iOS shows Konvo's in-app authentication sheet instead; the server ends that flow on the
// sheet's callback (NATIVE_CONTRACT.md, "KonvoNative"). Other native shells hand a top-level
// navigation to the system browser; desktop and browsers keep opening a new window.
import {
	getNativeMobilePlatform,
	isNativeMobileShell,
	NativePlugins,
} from '@app/features/platform/native_mobile/NativeMobileBridge';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';

/** `opened`: finishes elsewhere, so poll; `finished` / `cancelled`: the in-app sheet has closed. */
export type ConnectionSignInResult = 'opened' | 'finished' | 'cancelled';

function inAppSheet() {
	if (getNativeMobilePlatform() !== 'ios') return null;
	const native = NativePlugins.native();
	return native?.authenticate ? native : null;
}

/** Body for a connect request: asks the server to end on the in-app sheet's callback when one can show. */
export function connectionSignInBody<T extends object>(body: T): T & {native?: true} {
	return inAppSheet() ? {...body, native: true} : body;
}

export async function openConnectionSignIn(url: string): Promise<ConnectionSignInResult> {
	const sheet = inAppSheet();
	if (sheet?.authenticate) {
		try {
			await sheet.authenticate({url});
			return 'finished';
		} catch {
			// Closed by hand, or the sheet failed: the caller rechecks the connection either way.
			return 'cancelled';
		}
	}
	if (isNativeMobileShell()) {
		window.location.assign(url);
		return 'opened';
	}
	await openExternalUrl(url);
	return 'opened';
}
